import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, tap } from 'rxjs';
import { hasOpenDialogs } from '../../../../core/functions/dialog-helper';
import { AuthenticatedUser } from '../../../../core/models/authenticated-user';
import { CreditCardSummary } from '../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { CreditCardDialogService } from '../../../../core/services/commerce/credit-card-dialog/credit-card-dialog.service';
import { CreditCardSummaryService } from '../../../../core/services/commerce/credit-card-summary/credit-card-summary.service';
import { CreditCardService } from '../../../../core/services/commerce/credit-card/credit-card.service';
import { UserStateService } from '../../shared/services/user-state/user-state.service';
import { CardOnFileComponent } from './components/card-on-file/card-on-file.component';

@Component({
    selector: 'app-cards-on-file',
    templateUrl: './cards-on-file.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatButtonModule, CardOnFileComponent],
})
export class CardsOnFileComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    currentUser: AuthenticatedUser | null = this.userStateService.getUser();

    /**
     * The list of credit cards on file for the current user.
     */
    cardsOnFile: CreditCardSummary[] = [];

    constructor(
        private readonly dialog: MatDialog,
        private readonly userStateService: UserStateService,
        private readonly creditCardService: CreditCardService,
        private readonly creditCardDialogService: CreditCardDialogService,
        private readonly creditCardSummaryService: CreditCardSummaryService,
    ) {}

    ngOnInit(): void {
        this.loadCreditCards();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleAddCreditCardClick(): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        if (!this.currentUser) {
            throw new Error(
                'There is no current authenticated user. Cannot add a card on file without a user context.',
            );
        }

        this.creditCardDialogService
            .openCreateCardDialog(this.currentUser.id, null, this.cardsOnFile)
            .pipe(
                takeUntil(this.destroy$),
                tap((creditCardSummary: CreditCardSummary | null) => {
                    if (creditCardSummary != null) {
                        this.loadCreditCards();
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Loads the credit cards on file for the current user from the API and sorts them for display.
     * The cards are sorted with the default card first, then by expiration date (soonest to latest), then
     * by last four digits of the card number.
     */
    loadCreditCards(): void {
        if (!this.currentUser) {
            return;
        }

        this.creditCardService
            .getAll(this.currentUser.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                this.cardsOnFile =
                    this.creditCardSummaryService.orderCreditCards(
                        response.creditCards,
                    );
            });
    }
}
